import { useCallback, useEffect } from 'react';
import { useTerminalAccount } from '@src/context/AccountContext';
import { PunchFlowProvider, usePunchFlow } from '@src/context/PunchFlowContext';
import { useExternalUser } from '@src/context/ExternalUserContext';
import { useExternalTakeover } from '@src/hooks/useExternalTakeover';
import { useNetworkStatus } from '@src/context/NetworkStatusContext';
import { useOfflineTimeStatus } from '@src/hooks/useOfflineTimeStatus';
import { hostBridge } from '@src/messaging/hostBridge';
import { LocationInfo } from './LocationInfo/LocationInfo';
import { OfflineBanner } from './OfflineBanner';
import { OfflineLockoutScreen } from './OfflineLockoutScreen';
import { OfflineIntermediatePage } from './OfflineIntermediatePage';
import { LoadingPage } from './LoadingPage';
import { ExitedPage } from './ExitedPage';
import { PunchPad } from './PunchPad/PunchPad';
import { UserStatusPage } from './UserStatusPage/UserStatusPage';
import { PunchResultModal } from './PunchPad/PunchResultModal';
import { DepartmentSelectionModal } from './PunchPad/DepartmentSelectionModal';
import { RoleSelectionModal } from './PunchPad/RoleSelectionModal';
import { OfflineRoleSelectionModal } from './PunchPad/OfflineRoleSelectionModal';
import { OfflineDepartmentSelectionModal } from './PunchPad/OfflineDepartmentSelectionModal';
import { OfflinePunchRecordedModal } from './PunchPad/OfflinePunchRecordedModal';
import { AttestationModal } from './PunchPad/AttestationModal';
import { HealthCheckModal } from './PunchPad/HealthCheckModal';
import { TipsModal } from './PunchPad/TipsModal';
import { ErrorModal } from './PunchPad/ErrorModal';
import './PunchPadPage.css';

const STATUS_PHASES = new Set([
    'showing_status',
    'showing_intermediate',
    'executing_action'
]);

function PunchPadPageContent() {
    const {
        state,
        dispatch,
        actionContextRef,
        handlePin,
        handleIntermediateDismiss,
        handleOfflineSelection,
        confirmOfflinePunch
    } = usePunchFlow();
    const {
        externalUserId,
        resolvedEmployeeId,
        status: externalStatus,
        requestId: externalRequestId,
        reset: resetExternalUser
    } = useExternalUser();
    const { account } = useTerminalAccount();

    const isExternalUserMode = externalUserId !== null;

    const { isOnline, offlineSince } = useNetworkStatus();
    const offlineTimeStatus = useOfflineTimeStatus(offlineSince);

    // Reset the punch flow to idle — used by the external-user takeover to
    // clear the outgoing session before switching users.
    const resetToIdle = useCallback(
        () => dispatch({ type: 'DISMISS' }),
        [dispatch]
    );

    // Coordinate external-user takeover of the punch flow (API-4792). A POS
    // `set_external_user` is authoritative and takes over the terminal, but is
    // deferred while an in-flight punch write is settling. Also owns the PIN-pad
    // auto-skip once the mapping resolves (API-4790).
    const { takeoverPending, autoSkipPending, awaitingStatusAfterAutoSkip } =
        useExternalTakeover({
            isExternalUserMode,
            externalStatus,
            resolvedEmployeeId,
            requestId: externalRequestId,
            phase: state.phase,
            resetToIdle,
            submitResolvedPin: handlePin
        });

    // Stable callback used by ExitedPage to clean up and return to idle.
    const handleExitDone = useCallback(() => {
        resetExternalUser();
        dispatch({ type: 'DISMISS' });
    }, [resetExternalUser, dispatch]);

    // Offline lockout (API-4380): once the offline time limit is exceeded, no
    // punch pad is available. Evaluated proactively (not only on a punch
    // attempt) and re-checked on connectivity/time changes, so the terminal
    // returns to the normal pad automatically once back online.
    if (!isOnline && offlineTimeStatus.isLockedOut) {
        return <OfflineLockoutScreen />;
    }

    // Loading gate: keep the spinner up for the whole external-user auto-entry
    // transient, not just the mapping fetch — otherwise the PIN pad or the
    // outgoing user's screen paints for a frame during the switch. Covers:
    //   - the mapping fetch itself (`status === 'loading'`),
    //   - a takeover clearing a stable session before auto-skip (API-4792),
    //   - resolved-but-not-yet-auto-skipped (`phase === 'idle'`, this request),
    //   - the post-auto-skip status fetch (`phase === 'loading_status'`).
    // Scoped to the transient so the unresolved manual-PIN fallback and an
    // already-handed-off user still render normally. Must run before the
    // offline-intermediate early return below, otherwise a takeover arriving
    // during a stable `offline_intermediate` phase can paint the outgoing
    // offline screen before the reset effect lands (API-4792).
    if (
        externalStatus === 'loading' ||
        takeoverPending ||
        autoSkipPending ||
        awaitingStatusAfterAutoSkip
    ) {
        return <LoadingPage />;
    }

    // Offline intermediate page (API-4380 PR B): shown after a PIN is
    // submitted while offline, before handing off to the existing offline
    // dispatch (role selection / stored / etc.).
    if (state.phase === 'offline_intermediate') {
        return (
            <OfflineIntermediatePage
                onConfirm={confirmOfflinePunch}
                onCancel={() => dispatch({ type: 'DISMISS' })}
            />
        );
    }

    // Exited state: show brief confirmation, then clean up.
    if (state.phase === 'exited') {
        return <ExitedPage onDone={handleExitDone} />;
    }

    // In external-user mode, post-punch dismissals exit the session instead
    // of returning to the idle PIN pad.  The unresolved case falls back to the
    // normal PIN pad so the user can enter their ID manually.
    function onResultDismiss() {
        actionContextRef.current = null;
        const willExit = isExternalUserMode && externalStatus !== 'unresolved';
        if (willExit) {
            dispatch({ type: 'EXIT' });
        } else {
            dispatch({ type: 'DISMISS' });
        }
    }

    const isStatusPhase = STATUS_PHASES.has(state.phase);

    return (
        <div className="punch-pad-page">
            {isStatusPhase ? (
                <UserStatusPage />
            ) : (
                <>
                    <div className="punch-pad-page__left">
                        <LocationInfo>
                            {!isOnline && (
                                <OfflineBanner
                                    remainingDays={
                                        offlineTimeStatus.remainingDays
                                    }
                                    isApproaching={
                                        offlineTimeStatus.isApproaching
                                    }
                                />
                            )}
                        </LocationInfo>
                    </div>
                    <div className="punch-pad-page__right">
                        <PunchPad />
                    </div>
                </>
            )}

            {/* ---- Modals portal to body; rendered regardless of active view ---- */}

            {state.phase === 'showing_result' &&
                state.result.type === 'success' && (
                    <PunchResultModal
                        result={state.result}
                        firstName={
                            actionContextRef.current?.user.firstName ?? ''
                        }
                        onDismiss={onResultDismiss}
                    />
                )}

            {state.phase === 'showing_result' &&
                state.result.type === 'error' &&
                state.result.errorType === 'health_check_failed' && (
                    <ErrorModal
                        errorType="health_check_failed"
                        message={state.result.message}
                        onDismiss={onResultDismiss}
                    />
                )}

            {state.phase === 'showing_result' &&
                state.result.type === 'error' &&
                state.result.errorType !== 'health_check_failed' && (
                    <ErrorModal
                        errorType={state.result.errorType}
                        message={state.result.message}
                        onDismiss={onResultDismiss}
                    />
                )}

            {state.phase === 'showing_intermediate' &&
                state.response.type === 'tips_required' && (
                    <TipsModal
                        response={state.response}
                        onExecute={execute =>
                            dispatch({ type: 'INTERMEDIATE_RESOLVED', execute })
                        }
                    />
                )}

            {state.phase === 'showing_intermediate' &&
                state.response.type === 'department_selection_required' && (
                    <DepartmentSelectionModal
                        response={state.response}
                        onExecute={execute =>
                            dispatch({ type: 'INTERMEDIATE_RESOLVED', execute })
                        }
                    />
                )}

            {state.phase === 'showing_intermediate' &&
                state.response.type === 'role_selection_required' && (
                    <RoleSelectionModal
                        response={state.response}
                        onExecute={execute =>
                            dispatch({ type: 'INTERMEDIATE_RESOLVED', execute })
                        }
                        onDismiss={handleIntermediateDismiss}
                    />
                )}

            {state.phase === 'showing_intermediate' &&
                state.response.type === 'attestation_required' && (
                    <AttestationModal
                        response={state.response}
                        onExecute={execute =>
                            dispatch({ type: 'INTERMEDIATE_RESOLVED', execute })
                        }
                        onDismiss={handleIntermediateDismiss}
                    />
                )}

            {state.phase === 'showing_intermediate' &&
                state.response.type === 'health_check_required' && (
                    <HealthCheckModal
                        response={state.response}
                        onExecute={execute =>
                            dispatch({ type: 'INTERMEDIATE_RESOLVED', execute })
                        }
                        onDismiss={handleIntermediateDismiss}
                    />
                )}

            {/* Offline role/department selection (API-4649) — dispatch logic
                (API-4650) hands back to this modal layer after checking the
                LDR cache; Cancel returns to the punch pad without storing a
                punch. */}
            {state.phase === 'offline_role_selection' && (
                <OfflineRoleSelectionModal
                    roles={state.roles}
                    onSelect={(roleId, departmentId) =>
                        handleOfflineSelection({
                            roleId,
                            departmentId: departmentId ?? undefined
                        })
                    }
                    onCancel={() => dispatch({ type: 'DISMISS' })}
                />
            )}

            {state.phase === 'offline_department_selection' && (
                <OfflineDepartmentSelectionModal
                    departments={state.departments}
                    onSelect={departmentId =>
                        handleOfflineSelection({ departmentId })
                    }
                    onCancel={() => dispatch({ type: 'DISMISS' })}
                />
            )}

            {/* "Offline punch recorded" confirmation (API-4648) — shown once
                the offline dispatch/completion logic (API-4650) has stored
                the punch to IndexedDB. Auto-dismisses back to the punch pad,
                where the offline banner remains visible. */}
            {state.phase === 'offline_stored' && account && (
                <OfflinePunchRecordedModal
                    punchTime={state.punchTime}
                    locationTimezone={account.locationTimezone}
                    onDismiss={() => dispatch({ type: 'DISMISS' })}
                />
            )}
        </div>
    );
}

export function PunchPadPage() {
    const { account, isLoading } = useTerminalAccount();
    const isReady = !isLoading && account !== null;

    useEffect(() => {
        if (isReady) {
            hostBridge.ready();
        }
    }, [isReady]);

    return (
        <PunchFlowProvider>
            <PunchPadPageContent />
        </PunchFlowProvider>
    );
}
